import { lineString, point } from "@turf/helpers";
import pointToLineDistancePkg from "@turf/point-to-line-distance";
import rhumbDistancePkg from "@turf/rhumb-distance";

const pointToLineDistance = pointToLineDistancePkg.default || pointToLineDistancePkg;
const rhumbDistance = rhumbDistancePkg.default || rhumbDistancePkg;

/** Markers within this distance (km) of the route or stops are kept. */
export const ROUTE_NEARBY_DISTANCE_KM = 5;

const getAddressComponentName = (place, type) => {
	const legacyComponents = place?.address_components;
	if (Array.isArray(legacyComponents)) {
		return (
			legacyComponents.find((component) => component.types?.includes(type))?.long_name ||
			""
		);
	}

	const modernComponents = place?.addressComponents;
	if (Array.isArray(modernComponents)) {
		return (
			modernComponents.find((component) => component.types?.includes(type))?.longText ||
			modernComponents.find((component) => component.types?.includes(type))?.long_name ||
			""
		);
	}

	return "";
};

const getPlaceLatLng = (place) => {
	const location = place?.location || place?.geometry?.location;
	if (!location) {
		return { lat: null, lng: null };
	}

	const lat = typeof location.lat === "function" ? location.lat() : Number(location.lat);
	const lng = typeof location.lng === "function" ? location.lng() : Number(location.lng);

	return {
		lat: Number.isFinite(lat) ? lat : null,
		lng: Number.isFinite(lng) ? lng : null,
	};
};

const toPlainLatLng = (value) => {
	if (!value) return null;
	const lat = typeof value.lat === "function" ? value.lat() : Number(value.lat);
	const lng = typeof value.lng === "function" ? value.lng() : Number(value.lng);
	if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
	return { lat, lng };
};

export const extractLocation = (place) => {
	if (!place) {
		return { city: "", state: "", country: "", lat: null, lng: null, label: "" };
	}

	const { lat, lng } = getPlaceLatLng(place);

	return {
		city:
			getAddressComponentName(place, "locality") ||
			getAddressComponentName(place, "administrative_area_level_2") ||
			getAddressComponentName(place, "sublocality") ||
			"",
		state: getAddressComponentName(place, "administrative_area_level_1") || "",
		country: getAddressComponentName(place, "country") || "",
		lat,
		lng,
		label:
			place.formattedAddress ||
			place.formatted_address ||
			(typeof place.displayName === "string"
				? place.displayName
				: place.displayName?.text) ||
			place.name ||
			"",
		place,
	};
};

export const getPlaceGeometryLocation = (place) => {
	if (!place) return null;
	if (place.geometry?.location) return place.geometry.location;
	if (place.location) return place.location;
	return null;
};

export const buildLocationFilter = (fromPlace, toPlace, waypointPlaces = []) => {
	const all = [fromPlace, toPlace, ...waypointPlaces]
		.filter(Boolean)
		.map((place) => extractLocation(place));

	return {
		countries: [...new Set(all.map((item) => item.country).filter(Boolean))].join(","),
		states: [...new Set(all.map((item) => item.state).filter(Boolean))].join(","),
		districtCities: [...new Set(all.map((item) => item.city).filter(Boolean))].join(","),
	};
};

export const hasPlottableCoordinates = (record) => {
	const latitude = Number(record?.latitude);
	const longitude = Number(record?.longitude);
	return Number.isFinite(latitude) && Number.isFinite(longitude);
};

/** Flatten DirectionsResult into a polyline of { lat, lng }. */
export const getDirectionsPathCoordinates = (directionsResult) => {
	const route = directionsResult?.routes?.[0];
	if (!route) return [];

	if (Array.isArray(route.overview_path) && route.overview_path.length) {
		return route.overview_path.map(toPlainLatLng).filter(Boolean);
	}

	const coords = [];
	(route.legs || []).forEach((leg) => {
		(leg.steps || []).forEach((step) => {
			(step.path || []).forEach((pathPoint) => {
				const plain = toPlainLatLng(pathPoint);
				if (plain) coords.push(plain);
			});
		});
	});
	return coords;
};

/**
 * Keep markers near the driving route and/or stop points (From / To / waypoint).
 */
export const filterMarkersNearRoute = (
	markers = [],
	pathCoordinates = [],
	stopPoints = [],
	maxDistanceKm = ROUTE_NEARBY_DISTANCE_KM
) => {
	if (!markers.length) return [];

	const validStops = (stopPoints || [])
		.map((stop) => {
			if (!stop) return null;
			if (Number.isFinite(stop.lat) && Number.isFinite(stop.lng)) {
				return { lat: stop.lat, lng: stop.lng };
			}
			return toPlainLatLng(stop);
		})
		.filter(Boolean);

	const path = (pathCoordinates || []).filter(
		(coord) => Number.isFinite(coord?.lat) && Number.isFinite(coord?.lng)
	);

	if (path.length < 2 && !validStops.length) {
		return markers;
	}

	const routeLine =
		path.length >= 2
			? lineString(path.map((coord) => [coord.lng, coord.lat]))
			: null;

	return markers.filter((marker) => {
		const lat = Number(marker?.position?.lat);
		const lng = Number(marker?.position?.lng);
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;

		const markerPoint = point([lng, lat]);

		if (routeLine) {
			const distanceToRouteKm = pointToLineDistance(markerPoint, routeLine, {
				units: "kilometers",
			});
			if (distanceToRouteKm <= maxDistanceKm) return true;
		}

		return validStops.some((stop) => {
			const distanceToStopKm = rhumbDistance(
				markerPoint,
				point([stop.lng, stop.lat]),
				{ units: "kilometers" }
			);
			return distanceToStopKm <= maxDistanceKm;
		});
	});
};
